import { styled } from '@mui/material/styles'
import { useCallback, useEffect, useState } from 'react'
import { IconButton, Typography } from '@mui/material'
import { LIMITS } from '@weblite/permission'
import { ArrowRightIcon, PhoneVerificationIcon } from '$common/icons'
import { PrimaryButton } from '$common/buttons'
import { AuthContainer } from '$common/auth-container'
import { VerificationCodeInput } from '$common/verification-code-input'
import { Alert } from '$common/input/alert'
import {
  resendCode,
  VerificationSession,
  VerifyError,
  verifyPhone,
} from '$logic/auth'
import { goToAuthSignin } from '$logic/router'
import { formatDuration } from '$func/time'
import { t } from '$i18n'
import { useForceUpdate } from '$func/hooks'
import { toHumanReadablePhone } from '$func/phone'
import { dateWithOffset } from '$setup/time-offset'

const ERROR_MAPPER: Record<VerifyError, string> = {
  WRONG_CODE: t('signin.verification.wrongCodeEntered'),
  SERVER_ERROR: t('serverError'),
}

const Header = styled('div')`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: 16px 0 22px;
`

const Body = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  margin: 0;
  padding: 0 8px;
`

const OTPForm = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`

export function Verification({ phone, sessionId }: VerificationSession) {
  const forceUpdate = useForceUpdate()
  const [sentTime, setSentTime] = useState(dateWithOffset())
  const [resendCount, setResendCount] = useState(0)
  const [alertText, setAlertText] = useState('')

  useEffect(() => {
    const interval = setInterval(forceUpdate, 1000)
    return () => clearInterval(interval)
  }, [sentTime, forceUpdate])

  const resendMaxed = resendCount >= LIMITS.verificationCode.maxResends
  const remainingMillis =
    sentTime + LIMITS.verificationCode.resendInSeconds * 1000 - dateWithOffset()

  const handleResendCode = useCallback(async () => {
    if (await resendCode(phone, sessionId)) {
      setSentTime(dateWithOffset())
      setResendCount(count => count + 1)
    }
  }, [phone, sessionId])

  const handleCodeComplete = useCallback(
    async (code: string) => {
      const verificationError = await verifyPhone(phone, sessionId, code)
      if (verificationError) setAlertText(ERROR_MAPPER[verificationError])
    },
    [phone, sessionId],
  )

  const handleCodeChange = useCallback(
    async (code: string) => {
      if (code.length === LIMITS.verificationCode.length) {
        await handleCodeComplete(code)
      } else setAlertText('')
    },
    [handleCodeComplete],
  )

  const handleGoBack = useCallback(async () => {
    await goToAuthSignin()
  }, [])

  // TODO get default value of input from prop
  return (
    <AuthContainer>
      <Header>
        <IconButton
          edge="end"
          sx={{ color: 'neutral500' }}
          onClick={handleGoBack}
        >
          <ArrowRightIcon />
        </IconButton>
        <Typography variant="h3">{t('signin.verification.title')}</Typography>
      </Header>
      <Body>
        <PhoneVerificationIcon
          sx={{
            color: 'secondary700',
            width: '80px',
            height: '80px',
            marginBottom: '12px',
          }}
        />
        <Typography color="secondary700" variant="h3" mb="8px">
          {toHumanReadablePhone(phone)}
        </Typography>
        <Typography color="secondary700" variant="body2" mb="32px">
          {t('signin.verification.description')()}
        </Typography>
        <OTPForm>
          <VerificationCodeInput
            timeout={LIMITS.verificationCode.resendInSeconds * 1000}
            resendMaxed={resendMaxed}
            numFields={LIMITS.verificationCode.length}
            onChange={handleCodeChange}
            hasError={Boolean(alertText)}
          />
          <Alert
            variant="error"
            text={alertText}
            sxStyle={{
              display: alertText ? 'static' : 'none',
            }}
          />

          <PrimaryButton
            disabled={resendMaxed || remainingMillis > 0}
            sx={{ marginTop: '32px' }}
            fullWidth
            text={
              resendMaxed || remainingMillis <= 0
                ? t('signin.verification.resendCode')
                : t('signin.verification.validTillXMore', {
                    x: formatDuration(remainingMillis),
                  })
            }
            onClick={handleResendCode}
          />
        </OTPForm>
      </Body>
    </AuthContainer>
  )
}
